import { Fragment, useCallback, useState } from 'react'
import styled from 'styled-components/macro'

import { Layout, Button, useDialog } from '@loadsmart/loadsmart-ui'
import { Icon, Tooltip } from '@loadsmart/miranda-react'

import { StatusContent } from 'legacy/containers/ShipmentsPage/StatusContent'
import { StatusTitle } from 'legacy/containers/ShipmentsPage/StatusTitle'
import { screen } from 'legacy/styles/screen'

import { type Stop } from '../domain/Shipment'
import {
  getStopCheckClassName,
  getCheckedAt,
  getStopCheckTitle,
  getStopCheckInfo,
  getStopCheckIsCurrent,
  getStopCheckButtonLabel,
} from '../presenters/stopPresenter'
import StopCheckDialog from './ShipmentStopCheckDialog'

const StyledButton = styled(Button)`
  margin-left: 4em;
  margin-right: 1em;

  ${screen.md} {
    margin-left: 0;
  }
`

type ShipmentStopCheckedInOutProps = {
  type: 'in' | 'out'
  stop: Stop
  stopState: {
    index: number
    checkIn?: string
    checkOut?: string
  }
  lookupShipment: (uuid: string) => Promise<void>
  shipmentUUID: string
}

export function ShipmentStopCheckedInOut({
  type,
  stop,
  stopState,
  lookupShipment,
  shipmentUUID,
}: ShipmentStopCheckedInOutProps) {
  const [isLoading, setIsLoading] = useState(false)

  const { open, show, hide } = useDialog({
    open: false,
  })

  const title = getStopCheckTitle(type)
  const stopCheckIsCurrent = getStopCheckIsCurrent(type, stopState)
  const stopCheckClassName = getStopCheckClassName(type)
  const stopCheckInfo = getStopCheckInfo(stop, type)

  const onUpdateShipmentStatus = useCallback(() => {
    lookupShipment(shipmentUUID).then(() => {
      setIsLoading(false)
    })
  }, [lookupShipment, shipmentUUID])

  return (
    <Fragment>
      <Layout.Group
        className={`ls-PageShipmentsStatus-checkIn ${stopCheckIsCurrent}`}
        justify="space-between"
        align="center"
      >
        <Layout.Group>
          <StatusTitle>{title}</StatusTitle>
          <StatusContent className={stopCheckClassName}>
            <Layout.Group space="s" align="center">
              {getCheckedAt(stopCheckInfo, stop.facility?.timezone)}
              {Boolean(stopCheckInfo && stop.facility?.timezone) && (
                <Tooltip
                  placement="right"
                  trigger="hover"
                  message={
                    <Layout.Stack space="xs">
                      <span>{stop.facility?.timezone}</span>
                      <span>Facility location timezone</span>
                    </Layout.Stack>
                  }
                >
                  <Icon name="question" size="16px" color="color-text-tertiary" />
                </Tooltip>
              )}
            </Layout.Group>
          </StatusContent>
        </Layout.Group>
        {!stopCheckInfo ? (
          <StyledButton onClick={show} scale="small" loading={isLoading}>
            {getStopCheckButtonLabel(type)}
          </StyledButton>
        ) : null}
        {stopCheckInfo ? (
          <StyledButton variant="tertiary" onClick={show} scale="small" loading={isLoading}>
            Edit
          </StyledButton>
        ) : null}
      </Layout.Group>
      <StopCheckDialog
        shipmentUUID={shipmentUUID}
        stopId={stop.id}
        previousDateTime={stopCheckInfo || ''}
        isOpen={open}
        hideDialog={hide}
        stopType={type}
        callback={onUpdateShipmentStatus}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </Fragment>
  )
}
